import { Button, Form, Modal } from 'shared/components/ui';
import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'react-bootstrap';
import { useFacturacion } from '../../hooks/useFacturacion';
import { obrasSocialesAPI } from '../../../../core/api/api';
import { Loading } from '../../../../shared/components';
import { ToastContainer } from '../../../../shared/components/Toast';
import { exportToExcel } from '../../utils/excelUtils';
import { facturacionAPI } from '../../services/facturacionAPI';
import { Receipt, Search, Clear, Save, FileDownload, Refresh, Send, Visibility } from '@mui/icons-material';
import Header from '../../../../shared/components/layout/Header';
import Footer from '../../../../shared/components/layout/Footer';
import TablaFacturacion from '../TablaFacturacion/TablaFacturacion';
import './GrillaFacturacion.css';

const ExcelDropdown = ({ onExportar }) => {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <Button variant="success" size="sm" onClick={() => setAbierto(!abierto)} style={{ marginRight: '0.5rem' }}>
        <FileDownload sx={{ fontSize: 18, mr: 0.5 }} /> Exportar Excel ▾
      </Button>
      {abierto && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 4,
          background: 'white', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          border: '1px solid #e5e7eb', minWidth: 160, overflow: 'hidden'
        }}>
          <button onClick={() => { onExportar('general'); setAbierto(false); }}
            style={{ display: 'block', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', cursor: 'pointer' }}
            onMouseEnter={e => e.target.style.background = '#f3f4f6'}
            onMouseLeave={e => e.target.style.background = 'none'}
          >General</button>
          <button onClick={() => { onExportar('practicas'); setAbierto(false); }}
            style={{ display: 'block', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', cursor: 'pointer' }}
            onMouseEnter={e => e.target.style.background = '#f3f4f6'}
            onMouseLeave={e => e.target.style.background = 'none'}
          >Prácticas</button>
        </div>
      )}
    </div>
  );
};

const GrillaFacturacion = () => {
  const [obrasSociales, setObrasSociales] = useState([]);
  const [toast, setToast] = useState(null);
  const [filtros, setFiltros] = useState({
    obraSocial: '',
    mes: new Date().getMonth().toString().padStart(2, '0'),
    anio: new Date().getFullYear().toString(),
    nroFactura: '0',
    presentacion: new Date().toISOString().split('T')[0]
  });
  const { loading, consultas, practicas, totales, cargarFacturacion, toggleSeleccion, toggleAll } = useFacturacion();
  const [nroFactura, setNroFactura] = useState('');
  const [guardandoNro, setGuardandoNro] = useState(false);
  const [facturando, setFacturando] = useState(false);
  const [facturaAdjunta, setFacturaAdjunta] = useState(null);
  const [showFacturaModal, setShowFacturaModal] = useState(false);
  const [facturaUrl, setFacturaUrl] = useState('');

  useEffect(() => {
    cargarObrasSociales();
  }, []);

  const cargarObrasSociales = async () => {
    try {
      const response = await obrasSocialesAPI.getObrasSociales();
      setObrasSociales(response.data || []);
    } catch (error) {
      console.error('Error al cargar obras sociales:', error);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  const handleBuscar = async () => {
    if (!filtros.obraSocial || !filtros.mes) {
      alert('Seleccione la obra social y el mes');
      return;
    }
    await cargarFacturacion(filtros.obraSocial, parseInt(filtros.mes), parseInt(filtros.anio));
    // Consultar si hay factura adjunta para esta OS/período
    try {
      const factura = await facturacionAPI.obtenerFacturaOS(filtros.obraSocial, parseInt(filtros.mes), parseInt(filtros.anio));
      setFacturaAdjunta(factura?.existe ? factura : null);
    } catch {
      setFacturaAdjunta(null);
    }
  };

  useEffect(() => {
    const nroExistente = practicas.find(p => p.nroFactura)?.nroFactura;
    setNroFactura(nroExistente ? String(nroExistente) : '');
  }, [practicas]);

  const handleActualizarImportes = async () => {
    if (!filtros.obraSocial || !filtros.mes) {
      setToast({ message: 'Seleccioná la obra social y el mes para actualizar los importes', type: 'error' });
      return;
    }
    const mes = parseInt(filtros.mes);
    const anio = parseInt(filtros.anio);
    try {
      // Si el período ya tiene una factura cargada, pedir confirmación antes de recalcular
      let facturaPeriodo = null;
      try {
        const factura = await facturacionAPI.obtenerFacturaOS(filtros.obraSocial, mes, anio);
        facturaPeriodo = factura?.existe ? factura : null;
      } catch {
        facturaPeriodo = null;
      }
      if (facturaPeriodo) {
        const confirmar = window.confirm(
          `Este período ya tiene una factura cargada${facturaPeriodo.nroFactura ? ` (N° ${facturaPeriodo.nroFactura})` : ''}. ¿Querés recalcular los importes igual?`
        );
        if (!confirmar) return;
      }

      const resultado = await facturacionAPI.recalcularImportesObraSocial(filtros.obraSocial, mes, anio);
      const cantidad = resultado?.actualizados ?? 0;
      setToast({ message: `Importes actualizados correctamente (${cantidad} turnos)`, type: 'success' });

      await cargarFacturacion(filtros.obraSocial, mes, anio);
    } catch (error) {
      console.error('Error al actualizar importes:', error);
      setToast({ message: 'Error al actualizar importes', type: 'error' });
    }
  };

  const handleLimpiar = () => {
    setFiltros({
      obraSocial: '',
      mes: new Date().getMonth().toString().padStart(2, '0'),
      anio: new Date().getFullYear().toString(),
      nroFactura: '0',
      presentacion: new Date().toISOString().split('T')[0]
    });
  };

  const handleExportarExcel = (tipo) => {
    if (practicas.length === 0) {
      alert('No hay datos para exportar');
      return;
    }
    
    const obraSocialNombre = obrasSociales.find(os => os.idObraSocial === parseInt(filtros.obraSocial))?.obraSocial || '';
    const mesNombre = getMesNombre(filtros.mes);
    const fileName = `${obraSocialNombre}-${mesNombre}-${filtros.anio}.xlsx`;

    exportToExcel([], practicas, tipo, fileName);
  };

  const handleGuardarNroFactura = async () => {
    const nro = parseInt(nroFactura);
    if (!nro || nro <= 0) {
      setToast({ message: 'Ingrese un número de factura válido', type: 'error' });
      return;
    }
    setGuardandoNro(true);
    try {
      const idsTurnos = practicas.map(p => p.idTurno);
      await facturacionAPI.actualizarNroFactura(idsTurnos, nro);
      setToast({ message: `Nro. Factura ${nro} asignado correctamente`, type: 'success' });
      await cargarFacturacion(filtros.obraSocial, parseInt(filtros.mes), parseInt(filtros.anio));
    } catch (error) {
      setToast({ message: 'Error al asignar el número de factura', type: 'error' });
    } finally {
      setGuardandoNro(false);
    }
  };

  const handleFacturar = async () => {
    setFacturando(true);
    try {
      await facturacionAPI.facturarObraSocial();
      setToast({ message: 'Facturación realizada correctamente', type: 'success' });
      if (filtros.obraSocial && filtros.mes) {
        await cargarFacturacion(filtros.obraSocial, parseInt(filtros.mes), parseInt(filtros.anio));
      }
    } catch (error) {
      console.error('Error al facturar:', error);
      setToast({ message: 'Error al realizar la facturación', type: 'error' });
    } finally {
      setFacturando(false);
    }
  };

  const getMesNombre = (mes) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return meses[parseInt(mes) - 1];
  };

  return (
    <div className="grilla-facturacion">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <Receipt sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Facturación de Obras Sociales
          </h2>
          <span className="page-subtitle">Gestión de facturación y liquidaciones</span>
        </div>

      <Card className="mb-4 filters-card">
        <Card.Body className="filters-content">
          <Form>
            <div className="filters-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr auto' }}>
              <div className="filter-group">
                <label className="filter-label">Obra Social *</label>
                <select
                  name="obraSocial"
                  value={filtros.obraSocial}
                  onChange={handleFiltroChange}
                  className="filter-select"
                >
                  <option value="">Seleccione...</option>
                  {obrasSociales.map(os => (
                    <option key={os.idObraSocial} value={os.idObraSocial}>
                      {os.obraSocial}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label className="filter-label">Mes *</label>
                <select name="mes" value={filtros.mes} onChange={handleFiltroChange} className="filter-select">
                    <option value="01">Enero</option>
                    <option value="02">Febrero</option>
                    <option value="03">Marzo</option>
                    <option value="04">Abril</option>
                    <option value="05">Mayo</option>
                    <option value="06">Junio</option>
                    <option value="07">Julio</option>
                    <option value="08">Agosto</option>
                    <option value="09">Septiembre</option>
                    <option value="10">Octubre</option>
                    <option value="11">Noviembre</option>
                    <option value="12">Diciembre</option>
                  </select>
              </div>
              <div className="filter-group">
                <label className="filter-label">Año *</label>
                <select name="anio" value={filtros.anio} onChange={handleFiltroChange} className="filter-select">
                    {[2023, 2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
              </div>
              <div className="filter-actions" style={{ alignSelf: 'end', paddingBottom: '1rem' }}>
                <Button onClick={handleBuscar} disabled={loading} className="btn-search">
                  <Search sx={{ fontSize: 18, mr: 0.5 }} />
                  {loading ? 'Buscando...' : 'Buscar'}
                </Button>
                <Button onClick={handleLimpiar} className="btn-clear">
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} />
                  Limpiar
                </Button>
              </div>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {loading && <Loading />}

      {!loading && (consultas.length > 0 || practicas.length > 0) && (
        <TablaFacturacion 
          datos={[...consultas, ...practicas]}
          tipo="general"
          onToggleSeleccion={async (tipo, index) => {
            try {
              const result = await toggleSeleccion(tipo, index);
              setToast({ message: `${result.apellido} ${result.nombre}: ${result.facturable ? 'Marcado como facturable' : 'Desmarcado de facturación'}`, type: result.facturable ? 'success' : 'warning' });
            } catch {
              setToast({ message: 'Error al actualizar', type: 'error' });
            }
          }}
          onToggleAll={toggleAll}
          totales={{
            ...totales,
            practicasSubtotal: totales.consultasSubtotal + totales.practicasSubtotal,
            practicasCopago: totales.consultasCopago + totales.practicasCopago,
            practicasTotal: totales.consultasTotal + totales.practicasTotal
          }}
          acciones={
            <>
              {practicas.length > 0 && (
                <>
                  <input
                    type="number"
                    placeholder="Nro. Factura"
                    value={nroFactura}
                    onChange={(e) => setNroFactura(e.target.value)}
                    style={{ width: 120, padding: '0.25rem 0.5rem', fontSize: '0.8rem', borderRadius: 20, border: '1px solid #d1d5db', marginRight: '0.5rem' }}
                  />
                  <Button variant="primary" size="sm" onClick={handleGuardarNroFactura} disabled={guardandoNro || !nroFactura} style={{ marginRight: '0.5rem' }}>
                    <Save sx={{ fontSize: 18, mr: 0.5 }} /> {guardandoNro ? 'Asignando...' : 'Asignar Nº Factura'}
                  </Button>
                </>
              )}
              <ExcelDropdown onExportar={handleExportarExcel} />
              <Button variant="warning" size="sm" onClick={handleActualizarImportes}>
                <Refresh sx={{ fontSize: 18, mr: 0.5 }} /> Actualizar Importes
              </Button>
              <Button variant="danger" size="sm" onClick={handleFacturar} disabled={facturando} style={{ marginLeft: '0.5rem' }}>
                <Send sx={{ fontSize: 18, mr: 0.5 }} /> {facturando ? 'Facturando...' : 'Facturar'}
              </Button>
              {facturaAdjunta && (
                <Button
                  variant="info"
                  size="sm"
                  style={{ marginLeft: '0.5rem' }}
                  onClick={async () => {
                    try {
                      const response = await facturacionAPI.descargarFacturaOS(facturaAdjunta.archivo);
                      const blob = new Blob([response.data], { type: response.headers['content-type'] });
                      const url = URL.createObjectURL(blob);
                      setFacturaUrl(url);
                      setShowFacturaModal(true);
                    } catch {
                      setToast({ message: 'Error al cargar la factura', type: 'error' });
                    }
                  }}
                  title={`Factura Nº ${facturaAdjunta.nroFactura} - ${facturaAdjunta.razonSocial || ''}`}
                >
                  <Visibility sx={{ fontSize: 18, mr: 0.5 }} /> Ver Factura
                </Button>
              )}
            </>
          }
        />
      )}
      </div>
      <Footer />
      <ToastContainer toast={toast} onClose={() => setToast(null)} />

      {/* Modal para ver factura */}
      {showFacturaModal && (
        <Modal show onHide={() => { setShowFacturaModal(false); setFacturaUrl(''); }} size="xl">
            <Modal.Header closeButton>
              <Modal.Title>Factura Nº {facturaAdjunta?.nroFactura} — {facturaAdjunta?.razonSocial || ''}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ height: '72vh', padding: 0 }}>
              {facturaUrl && (
                <iframe
                  src={facturaUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="Vista previa de factura"
                />
              )}
            </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default GrillaFacturacion;
